<template>
  <div class="sign-in-page">
    <el-row justify="center">
      <el-col :xs="24" :sm="{ span: 12 }">
        <div class="sign-in-header">
          <div class="content">
            <router-link id="logo-link" to="/">
              <div class="elsa-logo">
                <img src="@/assets/images/elsa-dashboard-logo.svg" alt="Elsa Now" />
              </div>
            </router-link>
            <div class="switch-lang">
              <switch-language />
            </div>
          </div>
        </div>
        <div class="sign-in-form-section">
          <div class="content">
            <h1>{{ $t('login.title') }}</h1>
            <p v-if="isPaymentFirstLogin">{{ $t('login.payment_first_login_msg') }}</p>
            <h6 v-if="(viaLink || viaEmail) && !isPaymentFirstLogin">
              {{ $t('common.invited_by') }} {{ getOrganizationInviteName }}
            </h6>
            <div v-if="viaEmail && !isInviteAccepted" id="info-message">
              {{
                $t('login.accept_invitation', {
                  organizationName: getOrganizationInviteName
                })
              }}
            </div>
            <api-errors :errors="errors" />
            <el-form class="auth-form" :model="form" :rules="rules" ref="form">
              <elsa-input name="email" :label="$t('login.email_field.label')" :placeholder="$t('login.email_field.placeholder')" v-model.trim.lazy="form.email">
                {{ $t('login.email_field.label') }}
              </elsa-input>
              <elsa-input name="password" type="password" :label="$t('login.password_field.label')" :placeholder="$t('login.password_field.placeholder')" v-model.lazy="form.password">
                {{ $t('login.password_field.label') }}
              </elsa-input>
              <primary-button v-if="isPaymentFirstLogin" class="elsa-primary-button" @click.native="submitForm" type="submit" ref="submit" v-loading.fullscreen.lock="loading" v-bind:class="{ invite: viaEmail && !isInviteAccepted }">
                {{ $t('login.first_login') }}
              </primary-button>
              <primary-button v-else class="elsa-primary-button" @click.native="submitForm" type="submit" ref="submit" v-loading.fullscreen.lock="loading" v-bind:class="{ invite: viaEmail && !isInviteAccepted }">
                {{ viaEmail && !isInviteAccepted ? $t('login.login_and_accept_invitation') : $t('login.login') }}
              </primary-button>
            </el-form>
            <!--
            <div class="divider" />
            <secundary-button
              @click.native="handleNetworkLogin('facebook')"
              class="elsa-social-button"
              customIcon="facebook-logo"
              :iconLeft="true"
              v-loading="facebookLoading"
            >
              Facebook
            </secundary-button>
            -->
            <div>
              <a v-if="!isPaymentFirstLogin" id="forgot-password" @click="visibleForgotPasswordModal = true" href="javascript:void(0)">
                {{ $t('login.forgot_password') }}
              </a>
              <h2 v-if="!isPaymentFirstLogin" id="no-account">
                {{ $t('login.dont_have_account') }}
                <a href="https://elsaspeak.com/b2b/homepage" target="_blank" style="font-size: 16px">
                  {{ $t('login.create_account') }}
                </a>
                <!--<tertiary-button @click.native="visibleSignupModal = true" class="create-account">
                  {{ $t("login.create_account") }}
                </tertiary-button>-->
              </h2>
              <p class="terms">
                {{ $t('sign_up.by_signing_up_to_elsa') }}
                <a href="https://elsaspeak.com/terms" target="_blank">
                  {{ $t('common.terms_of_use') }}
                </a>
                {{ $t('common.and') }}
                <a href="https://elsaspeak.com/privacy" target="_blank">
                  {{ $t('common.privacy_policy') }}
                </a>
                {{ $t('sign_up.to_our') }}
              </p>
            </div>
          </div>
        </div>
      </el-col>
      <el-col class="hidden-xs-only" :span="12">
        <div id="pic-section">
          <div></div>
          <div class="content">
            <img src="@/assets/images/elsa-login-pic2.png" alt=""/>
            <div class="title">{{ $t('login.pic_title') }}</div>
            <p>{{ $t('login.pic_text') }}</p>
          </div>
          <div></div>
        </div>
      </el-col>
    </el-row>
    <recover-password
      :dialogVisible="visibleForgotPasswordModal"
      v-on:closed="visibleForgotPasswordModal = false"
    />
    <signup-modal
      :dialogVisible="visibleSignupModal"
      v-on:closed="handleCloseSignUp"
      :viaEmail="viaEmail"
      :organizationName="getOrganizationInviteName"
      :organizationEmail="getOrganizationInviteEmail"
      :organizationToken="getOrganizationInviteToken"
      :organizationFirstName="getOrganizationInviteFirstName"
      :organizationLastName="getOrganizationInviteLastName"
      :newUser="getOrganizationInviteNewUser"
      :inviteTokenValid="inviteTokenValid"
    />
    <confirm-account
      :dialogVisible="visibleConfirmModal"
    />
    <resend-confirmation-email
      :dialogVisible="visibleResendConfirmationEmail"
      v-on:closed="visibleResendConfirmationEmail = false"
    />
    <verify-info
      :dialogVisible="visibleVerifyInfo"
      :verifyInfoData="verifyInfoData"
      v-on:closed="visibleVerifyInfo = false"
    />
    <change-password
      :dialogVisible="visibleChangePassword"
      :current-password="form.password"
      :email="form.email"
      v-on:closed="handleCloseChangePassword"
    />
  </div>
</template>

<script>
import PrimaryButton from '@/components/common/buttons/PrimaryButton'
// import TertiaryButton from "@/components/common/buttons/TertiaryButton";
import ElsaInput from '@/components/common/Input'
import ApiErrors from '@/components/common/ApiErrors'
import RecoverPassword from '@/components/modals/recover-password/RecoverPassword'
import SignupModal from '@/components/modals/signup/SignupModal'
import ConfirmAccount from '@/components/modals/confirm-account/ConfirmAccount'
import ResendConfirmationEmail from '@/components/modals/confirm-account/ResendConfirmationEmail'
import ChangePassword from '@/components/modals/user-profile/ChangePassword'
import VerifyInfo from '@/components/modals/verify-facebook-info/VerifyInfo'
import { handleSubmitButtonState, handleFormValidation } from '@/helpers/formValidationHelper'
import SwitchLanguage from "@/components/common/SwitchLanguage";
import {
  compose,
  isEmpty,
  either,
  isNil,
  not,
  pathOr,
  always,
  path,
  prop,
  last
  // invoker,
} from 'ramda'
import { mapActions, mapGetters, mapState } from 'vuex'
// import { firebaseAuth } from "@/utils/firebase";
// import { warningMessage } from "@/utils/messages";
import { roleToRouteMap } from '@/constants/userRoles'
import forEach from 'ramda/src/forEach'

export default {
  components: {
    ElsaInput,
    PrimaryButton,
    RecoverPassword,
    SignupModal,
    ConfirmAccount,
    ResendConfirmationEmail,
    ApiErrors,
    VerifyInfo,
    ChangePassword,
    SwitchLanguage
    // TertiaryButton,
  },
  props: {},
  data() {
    return {
      // siteKey: process.env.VUE_APP_CAPTCHA_KEY,
      loading: false,
      inviteTokenValid: true,
      facebookLoading: false,
      form: { email: '' },
      visibleConfirmModal: false,
      visibleForgotPasswordModal: false,
      visibleResendConfirmationEmail: false,
      visibleSignupModal: false,
      visibleVerifyInfo: false,
      visibleChangePassword: false,
      verifyInfoData: {},
      errors: {},
      captcha: '',
      rules: {
        email: [
          {
            type: 'email',
            message: this.$t('login.email_field.validation_msg'),
            trigger: ['blur']
          },
          {
            message: this.$t('login.email_field.validation_msg_1'),
            trigger: ['blur'],
            validator: (rule, value, callback) => {
              const isValid = compose(not, either(isNil, isEmpty))
              if (!isValid(value)) {
                return callback(new Error(this.$t('login.email_field.callback_msg')))
              }
              return callback()
            }
          }
        ],
        password: [
          {
            message: this.$t('login.password_field.validation_msg'),
            trigger: ['blur'],
            validator: (rule, value, callback) => {
              const isValid = compose(not, either(isNil, isEmpty))
              if (!isValid(value)) {
                return callback(new Error(this.$t('login.password_field.validation_msg')))
              }
              return callback()
            }
          }
        ]
      },
      organizationId: null,
      isInviteAccepted: false
    }
  },
  methods: {
    async submitForm(ev) {
      ev.preventDefault()
      await this.recaptcha()
      this.loading = true
        handleFormValidation(this.$refs.form, this.handleSuccessLogin, () => {}).finally(() => {
          this.loading = false
      })
    },
    async recaptcha() {
      await this.$recaptchaLoaded()
      this.captcha = await this.$recaptcha('login')
    },
    async handleSuccessLogin() {
      const email = this.form.email
      const password = this.form.password
      try {
        await this.loginUser(email, password)
      } catch (errors) {
        handleSubmitButtonState(this.$refs.submit.$el, true)
        this.errors = errors
        return await this.$store.dispatch('auth/logout')
      } finally {
        this.loading = false
      }
    },
    async loginUser(email, password) {
      await this.$store.dispatch('auth/firebaseAuthenticate', {
        email,
        password,
        recaptcha: this.captcha
      })
      await this.acceptInvite()
      await this.loadUserData()

      if (this.isPaymentFirstLogin) {
        // required to change pass after first login
        this.visibleChangePassword = true
      } else {
        await this.$store.dispatch('auth/login')
        return this.redirectAfterLogin()
      }
    },
    async softLoginUser(email, password) {
      await this.$store.dispatch('auth/firebaseAuthenticate', {
        email,
        password,
        recaptcha: this.captcha
      })
      await this.acceptInvite()
      await this.loadUserData()
      // await this.$store.dispatch("auth/logout");
    },
    async acceptInvite() {
      try {
        if (this.viaEmail && this.getOrganizationInviteToken) {
          await this.$store.dispatch('invite/acceptInvite', {
            token: this.getOrganizationInviteToken
          })
          await this.$store.dispatch('invite/addInviteNotification', {
            organizationName: this.getOrganizationInviteName,
            success: true
          })
        }
      } catch (errors) {
        this.loading = false
        this.facebookLoading = false
        return (this.errors = errors)
      }
    },
    async loadUserData() {
      await this.$store.dispatch('organization/loadUserOrganizationsData')
      // const lastOrganizationId = compose(prop('id'), last, pathOr([], ['$store', 'getters', 'organization/userOrganizations']))(this)
      const userOrganizationsData = this.$store.getters['organization/userOrganizationsData']
      let selectedOrg = userOrganizationsData[0]
      // in case user belongs to many org, get org with is_origin_org = True or last org
      let organizationId = selectedOrg ? selectedOrg.organizationId : null;
      if (organizationId && !this.isElsaAdmin) {
        organizationId = Number(organizationId)
        await this.$store.dispatch('organization/setCurrentOrganization', { organizationId })
        await this.$store.dispatch('organization/setCurrentOrganizationId', { organizationId })
        this.organizationId = organizationId
      }
      this.loading = false
    },
    // handleNetworkLogin(network) {
    //   if (network === "google") {
    //     // Call to google auth
    //   } else if (network === "facebook") {
    //     this.facebookLoading = true;
    //     return this.facebookLogin()
    //       .then(() => this.loadUserData())
    //       .then(() => {
    //         this.$store.dispatch("auth/login");
    //         return this.$router.push({ name: "dashboard" });
    //       })
    //       .catch((errors) => {
    //         if (errors.message === "Please create an account") {
    //           return this.$store.dispatch("auth/facebookSignIn").then(() => {
    //             const user = firebaseAuth.currentUser;
    //             const verifyInfoData = compose(
    //               pathOr({}, ["providerData", 0]),
    //               invoker(0, "toJSON")
    //             )(user);
    //             return this.handleCloseSignUp({
    //               verifyInfo: true,
    //               verifyInfoData: {
    //                 ...verifyInfoData,
    //                 token: this.getOrganizationInviteToken,
    //                 organizationName: this.getOrganizationInviteName,
    //               },
    //             });
    //           });
    //         }
    //         this.errors = errors;
    //         return this.$store.dispatch("auth/logout");
    //       })
    //       .finally(() => {
    //         this.facebookLoading = false;
    //       });
    //   }
    // },
    handleCloseSignUp(data) {
      this.loading = true
      this.visibleSignupModal = false
      if (data && data.success && !data.viaEmail) {
        this.loading = false
      } else if (data && data.success && data.viaEmail) {
        if (data.email && data.password) {
          this.captcha = data.captcha;
          this.softLoginUser(data.email, data.password).then(() => {
            this.$store.dispatch('auth/login')
            this.loading = false
            return this.redirectAfterLogin()
          })
        }
      } else if (data && data.sendEmail) {
        this.visibleConfirmModal = false
        this.visibleResendConfirmationEmail = true
        this.loading = false
      } else if (data && data.verifyInfo) {
        this.visibleVerifyInfo = true
        this.verifyInfoData = { ...data.verifyInfoData }
        this.loading = false
      } else if (data && !data.sendEmail) {
        this.visibleConfirmModal = false
        this.loading = false
      }
      if (data && data.handleClose) {
        this.loading = false
      }
    },
    handleCloseChangePassword() {
      this.visibleChangePassword = false
      this.$store.dispatch('auth/login').then(() => this.redirectAfterLogin())
    },
    redirectAfterLogin() {
      let routeName = "dashboard";
      if(this.$route.query.redirect) {
        const path = this.$route.query.redirect;
        return window.location = path;
      } else {
        for (const role in roleToRouteMap) {
          const isRole = this[`is${role.charAt(0).toUpperCase()}${role.slice(1)}`];
          if (isRole) {
            routeName = roleToRouteMap[role];
            break;
          }
        }
      }
      if(this.SAonly && this.isOrganizationStudent) {
        routeName = roleToRouteMap.organizationSAStudent;
      }
      return this.$router.push({
          name: routeName,
          params: {
            organizationId: this.organizationId
          },
        })
    },
    ...mapActions({
      // facebookLogin: "auth/facebookLogin",
      setFirstLogin: 'user/setFirstLogin',
      validateToken: 'invite/validateTokenInvite'
    }),
    ...mapActions('auth', ['setSessionExpired'])
  },
  computed: {
    viaLink: always(false),
    viaEmail() {
      // return compose(not, either(isEmpty, isNil), pathOr({}, ['$route', 'query']))(this)
      return Object.keys(this.getOrganizationInvite).length > 0;
    },
    getOrganizationInvite() {
      try {
        const inviteParam = pathOr({}, ['$route', 'query', 'invite'])(this)
        const inviteBuffer = new Buffer(inviteParam, 'base64')
        const invite = JSON.parse(inviteBuffer.toString())
        const isFirstLogin = true
        this.setFirstLogin(isFirstLogin)
        return invite
      } catch (e) {
        return {}
      }
    },
    getOrganizationInviteName: path(['getOrganizationInvite', 'organization_name']),
    getOrganizationInviteEmail: path(['getOrganizationInvite', 'email']),
    getOrganizationInviteFirstName: path(['getOrganizationInvite', 'first_name']),
    getOrganizationInviteLastName: path(['getOrganizationInvite', 'last_name']),
    getOrganizationInviteToken: path(['getOrganizationInvite', 'token']),
    getOrganizationInviteNewUser: path(['getOrganizationInvite', 'new_user']),
    getPaymentFirstLogin () {
      try {
        const query = pathOr({}, ['$route', 'query', 'new'])(this);
        const buffer = new Buffer(query, 'base64');
        return JSON.parse(buffer.toString());
      } catch (e) {
        return {};
      }
    },
    getPaymentFirstLoginEmail: path(['getPaymentFirstLogin', 'email']),
    isPaymentFirstLogin () {
      return Object.keys(this.getPaymentFirstLogin).length > 0 && this.getPaymentFirstLoginEmail.length > 0;
    },
    getPrefillLoginInfo () {
      try {
        const query = pathOr({}, ['$route', 'query', 'info'])(this);
        const buffer = new Buffer(query, 'base64');
        return JSON.parse(buffer.toString());
      } catch (e) {
        return {};
      }
    },
    getPrefillLoginInfoEmail: path(['getPrefillLoginInfo', 'email']),
    getPrefillLoginInfoRedirect: path(['getPrefillLoginInfo', 'redirect']),
    isPrefillLoginInfo () {
      return Object.keys(this.getPrefillLoginInfo).length > 0 && this.getPrefillLoginInfoEmail.length > 0;
    },
    ...mapGetters('user', ['isElsaAdmin', 'getUserId']),
    ...mapGetters('organization', [
      'isOrganizationAdmin', 
      'isOrganizationAssociateAdmin', 
      'isOrganizationTeacher', 
      'isOrganizationStudent', 
      'isGeneralAdmin',
      'SAonly',
    ]),
    ...mapState('auth', ['sessionHasExpired'])
  },
  mounted() {
    this.$store.dispatch('auth/logout')
    if (this.isPaymentFirstLogin) {
      this.form.email = this.getPaymentFirstLoginEmail
    } else if (this.isPrefillLoginInfo) {
      this.form.email = this.getPrefillLoginInfoEmail
    } else if (this.viaEmail && !this.getOrganizationInviteNewUser) {
      this.form = {
        ...this.form,
        email: this.getOrganizationInviteEmail
      }
    }
  },
  watch: {
    getOrganizationInviteToken: {
      immediate: true,
      deep: true,
      handler(newToken) {
        const token = newToken
        if (token) {
          this.loading = true
          this.validateToken({ token })
            .then((res) => {
              const data = res.data
              if (data.new_user) {
                this.visibleSignupModal = true
              } else {
                this.form = {
                  ...this.form,
                  email: this.getOrganizationInviteEmail
                }
              }
              this.loading = false
            })
            .catch(() => {
              this.inviteTokenValid = false
              this.errors = { token: this.$t('login.invalid_token') }
              this.$router.push('Login')
              this.loading = false
            })
        }
      }
    },
    sessionHasExpired: {
      immediate: true,
      handler: function () {
        if (this.sessionHasExpired) {
          this.errors = [this.$t('common.warnings.session_expired')]
          this.setSessionExpired(false)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#no-account {
  margin-bottom: 0;
}

::v-deep .elsa-input .el-form-item__content .el-form-item__error {
  position: relative;
}

::v-deep .el-form .el-form-item .el-form-item__label {
  text-transform: capitalize;
  font-size: 18px;
  line-height: 24px;
  color: #121131;
}

.elsa-social-button.el-button {
  width: 100%;
}

.auth-page {
  .auth-form-section {
    .content .elsa-primary-button {
      width: 100%;
    }
    h6 {
      line-height: 1;
      margin-top: 8px;
    }
  }
}

.sign-in-page .sign-in-form-section {
  h1 {
    font-size: 32px;
  }
  .content {
    .elsa-primary-button {
      width: 160px;
      margin-top: 4px;

      &.invite {
        width: 320px;
      }
    }

    #forgot-password {
      font-size: 16px;
    }

    #no-account {
      text-align: left;
      margin-top: 8px;
    }

    .terms {
      font-size: 12px;
      text-align: left;
      margin: 48px 0 0;
      a {
        font-size: 12px;
      }
    }

    .separator {
      background: unset;
    }
  }
}

.auth-form {
  margin-bottom: 48px;
}

::v-deep .el-row {
  width: 100%;
}
</style>
